import { useEffect, useRef } from "react";
import dayjs from "dayjs";

/**
 * Hook to check for due reminders
 * @param {Array} reminders - Array of reminder objects from API
 * @param {Function} onReminderFired - Callback when a reminder fires (optional)
 */
const useReminderChecker = (reminders = [], onReminderFired) => {
  const firedRemindersRef = useRef(new Set());

  useEffect(() => {
    if (!reminders || reminders.length === 0) {
      return;
    }

    const checkReminders = () => {
      const now = dayjs();

      reminders.forEach((reminder) => {
        // Skip if already fired
        if (firedRemindersRef.current.has(reminder.id)) {
          return;
        }

        // Parse reminder date
        const reminderDate = dayjs(reminder.reminder_date);

        // Check if reminder is due (current time is past or equal to reminder time)
        if (now.isAfter(reminderDate) || now.isSame(reminderDate, "minute")) {
          // Mark as fired
          firedRemindersRef.current.add(reminder.id);

          // Call callback if provided
          if (onReminderFired) {
            onReminderFired(reminder);
          }
        }
      });
    };

    // Run an immediate check so overdue reminders fire right away
    checkReminders();

    // Then check every 30 seconds for newly due reminders
    const checkInterval = setInterval(checkReminders, 30000);

    // Cleanup interval on unmount
    return () => clearInterval(checkInterval);
  }, [reminders, onReminderFired]);

  // Utility to check if a reminder has fired
  const hasReminderFired = (reminderId) => {
    return firedRemindersRef.current.has(reminderId);
  };

  return { hasReminderFired };
};

export default useReminderChecker;
